import type { Location } from "history";
import { useCallback, useState } from "react";
import { t } from "ttag";

import { usePageTitle } from "metabase/hooks/use-page-title";
import { useDispatch, useSelector } from "metabase/redux";
import { forgotPassword } from "metabase/redux/auth";
import { Button } from "metabase/ui";

import { getIsEmailConfigured, getIsLdapEnabled } from "../../selectors";
import { AuthLayout } from "../AuthLayout";
import { ForgotPasswordForm } from "../ForgotPasswordForm";

import {
  InfoBody,
  InfoIcon,
  InfoIconContainer,
  InfoLink,
  InfoMessage,
} from "./ForgotPassword.styled";

type ViewType = "form" | "disabled" | "success";

interface ForgotPasswordQueryString {
  email?: string;
}

interface ForgotPasswordProps {
  location?: Location<ForgotPasswordQueryString>;
}

export const ForgotPassword = ({
  location,
}: ForgotPasswordProps): JSX.Element => {
  usePageTitle(t`Forgot password`, { titleIndex: 1 });

  const isEmailConfigured = useSelector(getIsEmailConfigured);
  const isLdapEnabled = useSelector(getIsLdapEnabled);
  const canResetPassword = isEmailConfigured && !isLdapEnabled;
  const initialEmail = location?.query?.email;

  const [view, setView] = useState<ViewType>(
    canResetPassword ? "form" : "disabled",
  );
  const dispatch = useDispatch();

  const handleSubmit = useCallback(
    async (email: string) => {
      await dispatch(forgotPassword(email)).unwrap();
      setView("success");
    },
    [dispatch],
  );

  return (
    <AuthLayout>
      {view === "form" && (
        <ForgotPasswordForm
          initialEmail={initialEmail}
          onSubmit={handleSubmit}
        />
      )}
      {view === "success" && <ForgotPasswordSuccess />}
      {view === "disabled" && <ForgotPasswordDisabled />}
    </AuthLayout>
  );
};

const ForgotPasswordSuccess = (): JSX.Element => {
  return (
    <InfoBody>
      <InfoIconContainer>
        <InfoIcon name="check" />
      </InfoIconContainer>
      <InfoMessage>
        {t` If the email exists, we'll send instructions on how to reset your password.`}
      </InfoMessage>
      <Button
        variant="filled"
        component="a"
        href="/auth/login"
      >{t`Back to sign in`}</Button>
    </InfoBody>
  );
};

const ForgotPasswordDisabled = (): JSX.Element => {
  return (
    <InfoBody>
      <InfoMessage>
        {t`Please contact an administrator to have them reset your password.`}
      </InfoMessage>
      <InfoLink to="/auth/login">{t`Back to sign in`}</InfoLink>
    </InfoBody>
  );
};
